'use client';

import { useEffect } from 'react';

function sanitizeFlagName(flagName: string): string {
  // Replace all unsupported characters with underscores
  return flagName.replace(/[.:+=&|><!()[\]{}^"'"`~*?\\]/g, '_');
}

/**
 * Reports a single feature flags object to Datadog RUM
 */
function reportFlagsObject(
  flags: Record<string, string | number | boolean>
): void {
  if (!flags || Object.keys(flags).length === 0) {
    return;
  }

  for (const [key, value] of Object.entries(flags)) {
    const sanitizedKey = sanitizeFlagName(key);
    window.DD_RUM?.addFeatureFlagEvaluation(sanitizedKey, value);
  }
}

/**
 * Reports all feature flags to Datadog RUM
 */
function reportAllFlags(
  ldFeatureFlags:
    | Record<string, string | number | boolean>
    | Record<string, string | number | boolean>[]
): void {
  if (
    !window.DD_RUM ||
    typeof window.DD_RUM.addFeatureFlagEvaluation !== 'function'
  ) {
    return;
  }

  // Normalize input to always work with an array of flag objects
  const flagsArray = Array.isArray(ldFeatureFlags)
    ? ldFeatureFlags
    : [ldFeatureFlags];

  // Report all LaunchDarkly feature flags to Datadog RUM
  for (const flags of flagsArray) {
    reportFlagsObject(flags);
  }
}

export function DDRumIntegration({
  featureFlags,
}: {
  featureFlags?:
    | Record<string, string | number | boolean>
    | Record<string, string | number | boolean>[];
}) {
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !featureFlags ||
      !Object.entries(featureFlags).length
    ) {
      return;
    }

    // Wait for Datadog RUM to be ready
    const attemptReport = () => {
      reportAllFlags(featureFlags);
    };

    // Try to report immediately if DD_RUM is already initialized
    attemptReport();

    // If DD_RUM has an onReady method, also register a callback
    if (
      window.DD_RUM &&
      // @ts-expect-error - onReady exists on DD_RUM but may not be in types
      typeof window.DD_RUM.onReady === 'function'
    ) {
      // @ts-expect-error - onReady exists on DD_RUM but may not be in types
      window.DD_RUM.onReady(attemptReport);
    }
  }, [featureFlags]);

  return null;
}
